/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import Modal from '../../../../components/Modal/Modal';
import RouteTypeToggle from './RouteTypeToggle';
import { mapFieldErrors } from '../../../../services/routing/routingScheduleApi';

// =============================================================================
// SubclusterFormModal — criar/editar uma rota (subcluster) — handoff §3.1/§3.2.
//
// Campos (todos opcionais): rótulo + horário de saída (T0) + limites de
// volumetria / tempo de deslocamento / tempo de serviço. NÃO inclui seletor de
// veículo (adiado: não há GET de vehicle_params ainda) nem técnico (atribuição
// é fluxo separado, assign-technician). 422 mapeia para erro por campo via
// mapFieldErrors.
//
// T0 (handoff 0055 §3): a PRESENÇA de route_departure_time no body dispara
// recompute síncrono pago (1 chamada Routes API) que reancora os ETAs — por
// isso o campo só entra no payload quando o operador ALTEROU o valor
// (dirty-check contra o baseline da abertura). '' limpa o horário (null).
// =============================================================================

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const WarnBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   line-height: 1.5;
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
`;

const FieldError = styled.span`
   font-size: 0.7rem;
   color: #c62828;
`;

const FieldHint = styled.span`
   font-size: 0.7rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#777'};
   line-height: 1.4;
`;

// '' / null → null; '12.5' → number. Mantém o campo opcional sem mandar 0.
function toNumberOrNull(value) {
   const trimmed = String(value ?? '').trim();
   if (trimmed === '') return null;
   const n = Number(trimmed);
   return Number.isFinite(n) ? n : null;
}

// 'HH:MM:SS' | 'HH:MM' | null → 'HH:MM' pro <input type="time">; inválido → ''.
function toInputTime(value) {
   if (!value) return '';
   const match = String(value).match(/^(\d{2}:\d{2})/);
   return match ? match[1] : '';
}

export default function SubclusterFormModal({
   mode,
   subcluster = null,
   onClose,
   onSubmit,
}) {
   const isEdit = mode === 'edit';

   const [label, setLabel] = useState('');
   const [departureTime, setDepartureTime] = useState('');
   const [routeType, setRouteType] = useState('round_trip');
   const [volumetry, setVolumetry] = useState('');
   const [travelTime, setTravelTime] = useState('');
   const [serviceTime, setServiceTime] = useState('');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});
   const [generalError, setGeneralError] = useState(null);

   // Baseline do T0 na abertura — o dirty-check decide se o campo entra no
   // payload (presença = recompute pago no backend; ver header).
   const initialDepartureRef = useRef('');
   // Baseline do route_type (handoff 0057 §3) — mesmo mecanismo do T0: a
   // presença de route_type no body dispara recompute síncrono (regrava
   // geometria/ETA/totais sem a perna de volta), então só entra quando o
   // operador realmente troca o tipo da rota.
   const initialRouteTypeRef = useRef('round_trip');

   // Preenche o form a cada abertura — em edição, com os valores atuais.
   useEffect(() => {
      setLabel(subcluster?.label ?? '');
      const t0 = toInputTime(subcluster?.routeDepartureTime);
      setDepartureTime(t0);
      initialDepartureRef.current = t0;
      const rt = subcluster?.routeType ?? 'round_trip';
      setRouteType(rt);
      initialRouteTypeRef.current = rt;
      setVolumetry(
         subcluster?.limits?.volumetry != null
            ? String(subcluster.limits.volumetry)
            : ''
      );
      setTravelTime(
         subcluster?.limits?.travelTime != null
            ? String(subcluster.limits.travelTime)
            : ''
      );
      setServiceTime(
         subcluster?.limits?.serviceTime != null
            ? String(subcluster.limits.serviceTime)
            : ''
      );
      setFieldErrors({});
      setGeneralError(null);
      setIsSubmitting(false);
   }, [subcluster]);

   const handleConfirm = async () => {
      setFieldErrors({});
      setGeneralError(null);
      setIsSubmitting(true);
      try {
         const payload = {
            subcluster_label: label.trim() || null,
            volumetry_limit_value: toNumberOrNull(volumetry),
            travel_time_limit_minutes: toNumberOrNull(travelTime),
            service_time_limit_minutes: toNumberOrNull(serviceTime),
         };
         // T0 só entra se o operador alterou (dirty-check); '' limpa (null).
         if (departureTime !== initialDepartureRef.current) {
            payload.route_departure_time =
               departureTime === '' ? null : departureTime;
         }
         // route_type só entra se o operador trocou o tipo (dirty-check) —
         // presença dispara recompute pago no backend (handoff 0057 §3).
         if (routeType !== initialRouteTypeRef.current) {
            payload.route_type = routeType;
         }
         await onSubmit(payload);
         // Sucesso: o pai fecha o modal e recarrega o snapshot.
      } catch (err) {
         const mapped = mapFieldErrors(err);
         setFieldErrors(mapped);
         const apiMessage = err?.response?.data?.message;
         if (Object.keys(mapped).length === 0) {
            setGeneralError(apiMessage ?? 'Erro ao salvar a rota.');
         }
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen
         onClose={isSubmitting ? () => {} : onClose}
         title={isEdit ? 'Editar rota' : 'Nova rota'}
         maxWidth="460px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
               >
                  {isSubmitting
                     ? 'Salvando…'
                     : isEdit
                     ? 'Salvar alterações'
                     : 'Criar rota'}
               </PrimaryButton>
            </>
         }
      >
         <Body>
            {generalError && <WarnBox>{generalError}</WarnBox>}

            <Field>
               <label htmlFor="hf-sub-label">Rótulo da rota</label>
               <input
                  id="hf-sub-label"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ex.: Rota Sul"
                  disabled={isSubmitting}
                  maxLength={120}
               />
               {fieldErrors.subcluster_label && (
                  <FieldError>{fieldErrors.subcluster_label}</FieldError>
               )}
            </Field>

            <Field>
               <label htmlFor="hf-sub-departure">
                  Horário de saída da base (T0)
               </label>
               <input
                  id="hf-sub-departure"
                  type="time"
                  value={departureTime}
                  onChange={(e) => setDepartureTime(e.target.value)}
                  disabled={isSubmitting}
               />
               <FieldHint>
                  Ancora os horários estimados de chegada (ETA) das paradas.
                  Definir ou alterar recalcula os ETAs da rota na hora.
               </FieldHint>
               {fieldErrors.route_departure_time && (
                  <FieldError>{fieldErrors.route_departure_time}</FieldError>
               )}
            </Field>

            <Field>
               <RouteTypeToggle
                  name="hf-sub-route-type"
                  value={routeType}
                  onChange={setRouteType}
                  disabled={isSubmitting}
               />
               <FieldHint>
                  Trocar o tipo recalcula o trajeto, a distância, a duração e os
                  ETAs desta rota na hora — <strong>sem reordenar as paradas</strong>{' '}
                  (a sequência definida na otimização é mantida).
               </FieldHint>
               {fieldErrors.route_type && (
                  <FieldError>{fieldErrors.route_type}</FieldError>
               )}
            </Field>

            <Field>
               <label htmlFor="hf-sub-vol">Limite de volumetria (m³)</label>
               <input
                  id="hf-sub-vol"
                  type="number"
                  step="0.01"
                  min="0"
                  value={volumetry}
                  onChange={(e) => setVolumetry(e.target.value)}
                  placeholder="opcional"
                  disabled={isSubmitting}
               />
               {fieldErrors.volumetry_limit_value && (
                  <FieldError>{fieldErrors.volumetry_limit_value}</FieldError>
               )}
            </Field>

            <Grid>
               <Field>
                  <label htmlFor="hf-sub-travel">
                     Tempo deslocamento (min)
                  </label>
                  <input
                     id="hf-sub-travel"
                     type="number"
                     min="0"
                     value={travelTime}
                     onChange={(e) => setTravelTime(e.target.value)}
                     placeholder="opcional"
                     disabled={isSubmitting}
                  />
                  {fieldErrors.travel_time_limit_minutes && (
                     <FieldError>
                        {fieldErrors.travel_time_limit_minutes}
                     </FieldError>
                  )}
               </Field>
               <Field>
                  <label htmlFor="hf-sub-service">Tempo serviço (min)</label>
                  <input
                     id="hf-sub-service"
                     type="number"
                     min="0"
                     value={serviceTime}
                     onChange={(e) => setServiceTime(e.target.value)}
                     placeholder="opcional"
                     disabled={isSubmitting}
                  />
                  {fieldErrors.service_time_limit_minutes && (
                     <FieldError>
                        {fieldErrors.service_time_limit_minutes}
                     </FieldError>
                  )}
               </Field>
            </Grid>
         </Body>
      </Modal>
   );
}

SubclusterFormModal.propTypes = {
   mode: PropTypes.oneOf(['create', 'edit']).isRequired,
   subcluster: PropTypes.object,
   onClose: PropTypes.func.isRequired,
   onSubmit: PropTypes.func.isRequired,
};
